import {
  faDownload,
  faMagnifyingGlass,
  faPlus,
  faSliders,
  faTableColumns,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useParams, useRouteMatch } from 'react-router-dom';

import { Breadcrumbs, Button, Input, PageHeader, Table, Toolbar } from '@feathr/components';

import conversionsColumns from '../columns/conversionsColumns';
import importsColumns from '../columns/importsColumns';
import peopleColumns from '../columns/peopleColumns';
import segmentColumns from '../columns/segmentColumns';
import PrototypeDoneModal from '../components/PrototypeDoneModal';
import type {
  ISampleConversion,
  ISampleImport,
  ISamplePerson,
  ISampleSegment,
} from '../sampleData';
import { sampleConversions, sampleImports, samplePeople, sampleSegments } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';
import { filterByText } from '../utils/filterByText';

import * as shellStyles from '../Shell/PrototypeShell.css';

const AUDIENCE_TABS = ['people', 'segments', 'imports', 'conversions'] as const;
type TAudienceTab = (typeof AUDIENCE_TABS)[number];

function isAudienceTab(value: string | undefined): value is TAudienceTab {
  return AUDIENCE_TABS.includes(value as TAudienceTab);
}

function getTitle(t: TFunction, tab: TAudienceTab): string {
  const titles: Record<TAudienceTab, string> = {
    people: t('People'),
    segments: t('Segments'),
    imports: t('Imports'),
    conversions: t('Conversions'),
  };
  return titles[tab];
}

function getCreateLabel(t: TFunction, tab: TAudienceTab): string {
  const labels: Record<TAudienceTab, string> = {
    people: t('Add person'),
    segments: t('Create segment'),
    imports: t('New import'),
    conversions: t('Add conversion'),
  };
  return labels[tab];
}

function AudiencePage(): JSX.Element {
  const { t } = useTranslation();
  const { tab: tabParam } = useParams<{ tab: string }>();

  if (!isAudienceTab(tabParam)) {
    return <Redirect to={'../people'} />;
  }

  return <AudienceTabPage t={t} tab={tabParam} />;
}

interface IAudienceTabPageProps {
  t: TFunction;
  tab: TAudienceTab;
}

function AudienceTabPage({ t, tab }: Readonly<IAudienceTabPageProps>): JSX.Element {
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Audience'), defaultPath: '/audience/people' },
    getTitle(t, tab),
    `/audience/${tab}`,
  );

  const peopleCols = useMemo(() => peopleColumns({ base, t }), [base, t]);
  const segmentCols = useMemo(() => segmentColumns({ base, t }), [base, t]);
  const importsCols = useMemo(() => importsColumns({ base, t }), [base, t]);
  const conversionsCols = useMemo(() => conversionsColumns({ base, t }), [base, t]);

  const filteredPeople = useMemo<ISamplePerson[]>(
    () => filterByText(samplePeople, search, (p) => [p.name, p.email]),
    [search],
  );
  const filteredSegments = useMemo<ISampleSegment[]>(
    () => filterByText(sampleSegments, search, (s) => [s.name]),
    [search],
  );
  const filteredImports = useMemo<ISampleImport[]>(
    () => filterByText(sampleImports, search, (i) => [i.name]),
    [search],
  );
  const filteredConversions = useMemo<ISampleConversion[]>(
    () => filterByText(sampleConversions, search, (c) => [c.name]),
    [search],
  );

  const filterElements = [
    <Box key={'search'} w={320}>
      <Input
        isClearable={true}
        onChange={(next?: string): void => setSearch(next ?? '')}
        placeholder={t('Search by name…')}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        type={'text'}
        value={search}
      />
    </Box>,
    <Toolbar align={'right'} key={'filters'}>
      <Button prefix={<FontAwesomeIcon icon={faSliders} />} type={'secondary'}>
        {t('Filters')}
      </Button>
      <Button prefix={<FontAwesomeIcon icon={faTableColumns} />} type={'secondary'}>
        {t('Customize columns')}
      </Button>
    </Toolbar>,
  ];

  const actions = (
    <Toolbar>
      <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
        {t('Export as CSV')}
      </Button>
      <Button
        onClick={(): void => setDoneOpen(true)}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {getCreateLabel(t, tab)}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader
        actions={actions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={getTitle(t, tab)}
      />
      <div className={shellStyles.body}>
        {tab === 'people' && (
          <Table<ISamplePerson>
            columns={peopleCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredPeople}
          />
        )}
        {tab === 'segments' && (
          <Table<ISampleSegment>
            columns={segmentCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredSegments}
          />
        )}
        {tab === 'imports' && (
          <Table<ISampleImport>
            columns={importsCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredImports}
          />
        )}
        {tab === 'conversions' && (
          <Table<ISampleConversion>
            columns={conversionsCols}
            filterElements={filterElements}
            filterLabel={<></>}
            idKey={'id'}
            isPaginated={false}
            items={filteredConversions}
          />
        )}
      </div>

      <PrototypeDoneModal
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={getCreateLabel(t, tab)}
      />
    </>
  );
}

export default AudiencePage;
